import React, { useEffect, useState } from 'react';
import * as cronParser from 'cron-parser';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import * as EmailValidator from 'email-validator';
import { GET_TEST_EXECUTIONS_BY_ID } from '../../../GraphQL/TestExecutions/TRFX/Queries';
import { EDIT_SCHEDULED_TEST_EXECUTION } from '../../../GraphQL/TestExecutions/TRFX/Mutations';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import './Schedule.css';
import { get } from '../../../Utils/helpers';

function ScheduleForm() {
  const params = useParams();
  const navigate = useNavigate();

  const [runMutation] = useMutation(EDIT_SCHEDULED_TEST_EXECUTION);
  const [isScheduleParam, setIsScheduleParam] = useState(false);
  const [frequency, setFrequency] = useState('');
  const [content, setContent] = useState('Start schedule');
  const [formTitle, setFormTitle] = useState('Form schedule');
  const [email, setEmail] = useState('');
  const [saveMode, setSaveMode] = useState(false);
  const [showEmailList, setShowEmailList] = useState(false);
  const [notificationType, setNotificationType] = useState([]);

  const errorHandler = (message) => {
    window.showMessage(message, 'error');
  };

  const validateCronExpression = (cronExpression) => {
    try {
      cronParser.parseExpression(cronExpression);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        e: e.message,
      };
    }
  };

  const { refetch } = useQuery(GET_TEST_EXECUTIONS_BY_ID, {
    variables: { id: params.testExecutionId },
    fetchPolicy: 'network-only', // This ensures we always fetch from the network
    onCompleted: (data) => {
      const res = get(data, 'testsExecutionFindById');
      if (res) {
        setIsScheduleParam(res.isScheduled);
        setFrequency(res.frequency);
        setFormTitle(`Schedule - ${res.name}`);
        setEmail((res.email || []).toString());
        if (res.notificationType === 'both') {
          setNotificationType(['opsgenie', 'email']);
          setShowEmailList(true);
        } else {
          setNotificationType([res.notificationType]);
          setShowEmailList(res.notificationType === 'email');
        }
        if (res.isScheduled) {
          setContent('Stop schedule');
        }
      } else {
        errorHandler('Invalid tests execution.');
        navigate('/trfx-test-executions');
      }
    },
    onError: (error) => errorHandler(error.message),
  });

  useEffect(() => {
    // This ensures the data is fetched whenever the testExecutionId parameter changes
    if (params.testExecutionId) {
      refetch();
    }
  }, [refetch, params.testExecutionId]);

  // useEffect(() => {
  //   refetch();
  // }, [refetch, params.testExecutionId]);
  const buttonSave = (event) => {
    event.preventDefault();
    const reviewMailStates = ['both', 'email'];
    setSaveMode(false);
    let notificationTypeVar = 'opsgenie';
    if (notificationType.length === 2) {
      notificationTypeVar = 'both';
    } else if (
      notificationType.length === 1 &&
      notificationType[0] === 'email'
    ) {
      notificationTypeVar = 'email';
    }

    let inputMails = email.replace(/ /g, '');
    inputMails = inputMails.toLowerCase();
    inputMails = inputMails.split(',');
    if (reviewMailStates.includes(notificationTypeVar)) {
      for (const mail of inputMails) {
        const isValid = EmailValidator.validate(mail);
        if (!isValid) {
          return errorHandler(`Wrong format for email ${mail}`);
        }
      }
    }

    const cronExpression = frequency.toString().trim();

    const isValidCronExpression = validateCronExpression(cronExpression);

    if (params.testExecutionId && isValidCronExpression.ok) {
      runMutation({
        variables: {
          id: params.testExecutionId,
          record: {
            isScheduled: true,
            scheduleStartDate: new Date(),
            frequency: cronExpression,
            cronExpression,
            email: inputMails,
            notificationType: notificationTypeVar,
          },
        },
      })
        .then((r) => {
          const response = get(
            r,
            'data.testsExecutionUpdateScheduleCreateBridge'
          );
          if (get(response, 'error')) {
            window.flash(
              `Test execution could not be updated. Error: ${get(
                response,
                'error.message'
              )}`,
              'error'
            );
            return;
          }
          window.hideMessage();
          window.flash('Tests execution successfully scheduled!', 'success');
          setContent('Stop schedule');
        })
        .catch((error) => {
          window.hideMessage();
          window.flash(
            'An error occurred. Tests execution could not be updated',
            'error'
          );
        });
    } else {
      window.showMessage(
        `Cron expression is invalid. Error: ${isValidCronExpression.e}`,
        'error'
      );
    }
  };

  const buttonSetSchedule = (isSchedule) => {
    runMutation({
      variables: {
        id: params.testExecutionId,
        record: {
          isScheduled: isSchedule,
          scheduleStartDate: new Date(),
        },
      },
    })
      .then((r) => {
        const response = get(
          r,
          'data.testsExecutionUpdateScheduleCreateBridge'
        );
        if (get(response, 'error')) {
          window.flash(
            `Test execution could not be updated. Error: ${get(
              response,
              'error.message'
            )}`,
            'error'
          );
          return;
        }
        setIsScheduleParam(isSchedule);
        setContent(isSchedule === true ? 'Stop schedule' : 'Start schedule');
        window.hideMessage();
        window.flash('Tests execution successfully updated!', 'success');
      })
      .catch((error) => {
        window.hideMessage();
        window.flash(
          `An error occurred. Tests execution could not be updated. Error: ${error.message}`,
          'error'
        );
      });
  };

  return (
    <div className='d-flex justify-content-center'>
      <div className='e-card' style={{ width: '70%' }}>
        <div className='e-card-header mt-2' style={{ width: '100%' }}>
          <div className='e-card-header-caption'>
            <div className='e-card-header-title d-flex justify-content-center'>
              <h5>{formTitle}</h5>
            </div>
          </div>
        </div>
        <form className='p-3'>
          <div className='field mb-3'>
            <span className='p-float-label w-20'>
              <InputText
                id='frequency'
                value={frequency}
                onChange={(e) => {
                  setFrequency(e.target.value);
                  setSaveMode(true);
                }}
                className='w-full'
              />
              <label htmlFor='frequency'>Set frequency format</label>
            </span>
          </div>
          <div className='field mb-3'>
            <div
              style={{
                display: 'flex',
                columnGap: '20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  inputId='opsgenie'
                  checked={notificationType.includes('opsgenie')}
                  onChange={(e) => {
                    const newTypes = e.checked
                      ? [...notificationType, 'opsgenie']
                      : notificationType.filter((t) => t !== 'opsgenie');
                    setNotificationType(newTypes);
                    setSaveMode(true);
                  }}
                />
                <label htmlFor='opsgenie' className='ml-2 mb-0'>
                  Opsgenie
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  inputId='email'
                  checked={notificationType.includes('email')}
                  onChange={(e) => {
                    const newTypes = e.checked
                      ? [...notificationType, 'email']
                      : notificationType.filter((t) => t !== 'email');
                    setNotificationType(newTypes);
                    setShowEmailList(e.checked);
                    setSaveMode(true);
                  }}
                />
                <label htmlFor='email' className='ml-2 mb-0'>
                  Email
                </label>
              </div>
            </div>
          </div>
          {showEmailList && (
            <div className='field mb-3'>
              <span className='p-float-label w-50'>
                <InputText
                  id='email-input'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setSaveMode(true);
                  }}
                  className='w-full'
                />
                <label htmlFor='email-input'>Notification e-mail</label>
              </span>
            </div>
          )}
          <div className='field'>
            <table className='table table-striped table-hover'>
              <thead className='bg-primary text-white'>
                <tr>
                  <th>
                    <b>Field</b>
                  </th>
                  <th>
                    <b>Values</b>
                  </th>
                  <th>
                    <b>Examples</b>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <p className='mb-0'>Minutes</p>
                  </td>
                  <td>
                    <p className='mb-0'>0-59</p>
                  </td>
                  <td>
                    <ul className='p-0 m-0 list-none'>
                      <li className='mb-2'>
                        Run every 10 minutes
                        <code className='ml-2 p-2 surface-ground border-round'>
                          */10 * * * *
                        </code>
                      </li>
                      <li className='mb-2'>
                        Run at the minute &apos;5&apos; each hour
                        <code className='ml-2 p-2 surface-ground border-round'>
                          5 * * * *
                        </code>
                      </li>
                      <li className='mb-2'>
                        Run every minute between 3 and 7
                        <code className='ml-2 p-2 surface-ground border-round'>
                          3-7 * * * *
                        </code>
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className='mb-0'>Hours</p>
                  </td>
                  <td>
                    <p className='mb-0'>0-23</p>
                  </td>
                  <td>
                    <ul className='p-0 m-0 list-none'>
                      <li className='mb-2'>
                        Run at minute 6 every 2 hours
                        <code className='ml-2 p-2 surface-ground border-round'>
                          6 */2 * * *
                        </code>
                      </li>
                      <li className='mb-2'>
                        Run at minutes 0 and 30 of 2PM
                        <code className='ml-2 p-2 surface-ground border-round'>
                          0,30 14 * * *
                        </code>
                      </li>
                      <li className='mb-2'>
                        Run every minute of hours 3AM, 5AM, 7PM
                        <code className='ml-2 p-2 surface-ground border-round'>
                          * 3,5,19 * * *
                        </code>
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className='mb-0'>Day-of-month</p>
                  </td>
                  <td>
                    <p className='mb-0'>1-31, L</p>
                  </td>
                  <td>
                    <ul className='p-0 m-0 list-none'>
                      <li className='mb-2'>
                        Run at the minute 0 of the hours 12AM, 12M every 2 days
                        <code className='ml-2 p-2 surface-ground border-round'>
                          0 0,12 */2 * *
                        </code>
                      </li>
                      <li className='mb-2'>
                        Run at 1PM on the day 10 and the last day of each month
                        <code className='ml-2 p-2 surface-ground border-round'>
                          0 13 10,L * *
                        </code>
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className='mb-0'>Month</p>
                  </td>
                  <td>
                    <p className='mb-0'>1-12 or JAN-DEC</p>
                  </td>
                  <td>
                    <ul className='p-0 m-0 list-none'>
                      <li className='mb-2'>
                        Run on 3PM every last day of JAN, FEB, MAR and APR
                        <code className='ml-2 p-2 surface-ground border-round'>
                          0 15 L JAN,FEB,3,APR *
                        </code>
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className='mb-0'>Day-of-week</p>
                  </td>
                  <td>
                    <p className='mb-0'>1-7 or SUN-SAT</p>
                  </td>
                  <td>
                    <p></p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            className=' gap-2 mt-5'
            style={{
              display: 'flex',
              justifyContent: 'center',
              columnGap: '20px',
            }}
          >
            <Button
              label='Close'
              icon='pi pi-times'
              onClick={() => navigate('/trfx-test-executions')}
              className='p-button-outlined'
            />
            {saveMode && (
              <Button
                label='Save'
                icon='pi pi-save'
                onClick={(event) => buttonSave(event)}
              />
            )}
            {!saveMode && (
              <Button
                label={content}
                icon={isScheduleParam ? 'pi pi-pause' : 'pi pi-play'}
                onClick={() => buttonSetSchedule(!isScheduleParam)}
              />
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ScheduleForm;
